import { Injectable } from '@angular/core';

export interface IRecording {
  blob: Blob;
  ext: string;
}

// Registrazione vocale via MediaRecorder. Lo stato (isRec/isTranscribing) vive nel chiamante.
@Injectable({ providedIn: 'root' })
export class RecorderService {
  private _mediaRec: MediaRecorder | null = null;
  private _stream: MediaStream | null = null;
  private _chunks: Blob[] = [];

  async start(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      throw new Error('Registrazione non supportata da questo browser. Usa Chrome, Edge o Safari recenti.');
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      this._stream = stream;
      const mime = this._pickMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      this._chunks = [];
      mr.ondataavailable = (e): void => {
        if (e.data && e.data.size > 0) {
          this._chunks.push(e.data);
        }
      };
      mr.start(1000);
      this._mediaRec = mr;
    } catch (err) {
      this._cleanup();
      const e = err as DOMException;
      const msg =
        e.name === 'NotAllowedError'
          ? 'Permesso microfono negato. Abilitalo dalle impostazioni del browser.'
          : e.name === 'NotFoundError'
            ? 'Nessun microfono rilevato.'
            : 'Impossibile avviare la registrazione: ' + (e.message || '');
      throw new Error(msg);
    }
  }

  // Ferma e restituisce il blob registrato (null se troppo breve / silenzioso).
  stop(): Promise<IRecording | null> {
    return new Promise((resolve) => {
      const mr = this._mediaRec;
      if (!mr || mr.state === 'inactive') {
        this._cleanup();
        resolve(null);
        return;
      }
      mr.onstop = (): void => {
        const type = mr.mimeType || 'audio/webm';
        const blob = new Blob(this._chunks, { type });
        this._cleanup();
        if (blob.size < 800) {
          resolve(null);
          return;
        }
        const ext = type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm';
        resolve({ blob, ext });
      };
      mr.stop();
    });
  }

  private _pickMime(): string {
    const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    for (const m of cands) {
      if (window.MediaRecorder?.isTypeSupported?.(m)) {
        return m;
      }
    }
    return '';
  }

  private _cleanup(): void {
    this._stream?.getTracks().forEach((t) => t.stop());
    this._stream = null;
    this._mediaRec = null;
  }
}
